var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { stateManager } from '../store/stateManager';
import customEventHandler from './customEvents';
import { markAsCreated } from './hooks/useDuplicateAnnounce';
/**
 * Reorders `items` to match `idOrder` (the full manipulative id order captured
 * at 'reorder' entry time). Items whose id isn't found in `idOrder` (shouldn't
 * normally happen) are appended at the end in their original relative order.
 */
function reorderByIds(items, idOrder) {
    var indexOf = new Map(idOrder.map(function (id, i) { return [id, i]; }));
    var known = [];
    var unknown = [];
    items.forEach(function (item) {
        var _a;
        if (indexOf.has((_a = item.data.id) !== null && _a !== void 0 ? _a : ''))
            known.push(item);
        else
            unknown.push(item);
    });
    known.sort(function (a, b) { var _a, _b; return indexOf.get((_a = a.data.id) !== null && _a !== void 0 ? _a : '') - indexOf.get((_b = b.data.id) !== null && _b !== void 0 ? _b : ''); });
    return __spreadArray(__spreadArray([], known, true), unknown, true);
}
/**
 * Apply undo operation by restoring old values from state changes
 * @param stateChanges - Array of state changes from undo()
 * @param setters - Object containing state setter functions
 */
export function applyUndo(stateChanges, setters) {
    // Pre-pass: collect every manipulative id whose duplicate must be removed
    // in this undo batch. We then apply ALL filters in a single
    // setManipulative call below — this avoids any subtle issue where chained
    // setManipulative calls in a forEach loop don't compose correctly when
    // the undo contains both the parent NL duplicate AND its cascade-child
    // JB / customTick duplicates. Trust `oldData.data.id` as the canonical
    // id (the entry's `old` field is `JSON.stringify(newItem)`) so we're
    // robust to any mismatch between `change.id` and the stored item.
    var duplicateManipulativeIdsToRemove = new Set();
    stateChanges.forEach(function (change) {
        var _a;
        if (change.propertyName !== 'duplicate')
            return;
        var t = change.type;
        var isManipDup = t === 'numberFrame_' ||
            t.startsWith('dice') ||
            t.startsWith('patternShapes') ||
            t.startsWith('spinner') ||
            t.startsWith('numberChart') ||
            t.startsWith('timer') ||
            t.startsWith('analog') ||
            t.startsWith('fractionTiles') ||
            t.startsWith('manipulativeGroup') ||
            t.startsWith('nmpGroup') ||
            t.startsWith('deck') ||
            t.startsWith('arrayModel') ||
            t.startsWith('numberRacks') ||
            t.startsWith('linkingCube') ||
            t.startsWith('numberPiece') ||
            t.startsWith('moneyPiece') ||
            t.startsWith('coloredTiles') ||
            t.startsWith('linearPiece') ||
            t.startsWith('jumpBar') ||
            t.startsWith('customTick') ||
            t.startsWith('numberLine') ||
            t.startsWith('numberFrame_') ||
            t.startsWith('counter_') ||
            t.startsWith('coinsAndCounters') ||
            t.startsWith('protractor');
        if (!isManipDup)
            return;
        var oldStr = change.old;
        var resolvedId;
        try {
            var oldObj = typeof oldStr === 'string' ? JSON.parse(oldStr) : oldStr;
            resolvedId = (_a = oldObj === null || oldObj === void 0 ? void 0 : oldObj.data) === null || _a === void 0 ? void 0 : _a.id;
        }
        catch (_b) {
            resolvedId = undefined;
        }
        duplicateManipulativeIdsToRemove.add(resolvedId !== null && resolvedId !== void 0 ? resolvedId : String(change.id));
    });
    stateChanges.forEach(function (change) {
        var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l;
        var type = change.type, id = change.id, propName = change.propertyName, old = change.old;
        var propertyName = propName;
        console.log('applyUndo', change);
        if (propertyName === 'reorder') {
            // Restore manipulative array order to what it was before the bring-to-front
            var idOrder_1 = (typeof old === 'string' ? JSON.parse(old) : old);
            setters.setManipulative(function (prev) { return reorderByIds(prev, idOrder_1); });
            return;
        }
        if (propertyName === 'mixedDelete') {
            // Restore all deleted items (text, annotations, shades, tables) at their original indices
            var data_1 = typeof old === 'string' ? JSON.parse(old) : old;
            if (((_a = data_1.texts) === null || _a === void 0 ? void 0 : _a.length) > 0) {
                setters.setText(function (prev) {
                    var newArray = __spreadArray([], prev, true);
                    __spreadArray([], data_1.texts, true).sort(function (a, b) { return b.index - a.index; }).forEach(function (_a) {
                        var index = _a.index, item = _a.data;
                        newArray.splice(index, 0, item);
                    });
                    return newArray;
                });
            }
            if (((_b = data_1.annotations) === null || _b === void 0 ? void 0 : _b.length) > 0) {
                setters.setAnnotations(function (prev) {
                    var newArray = __spreadArray([], prev, true);
                    __spreadArray([], data_1.annotations, true).sort(function (a, b) { return b.index - a.index; }).forEach(function (_a) {
                        var index = _a.index, item = _a.data;
                        newArray.splice(index, 0, item);
                    });
                    return newArray;
                });
            }
            if (((_c = data_1.shades) === null || _c === void 0 ? void 0 : _c.length) > 0) {
                setters.setShades(function (prev) {
                    var newArray = __spreadArray([], prev, true);
                    __spreadArray([], data_1.shades, true).sort(function (a, b) { return b.index - a.index; }).forEach(function (_a) {
                        var index = _a.index, item = _a.data;
                        newArray.splice(index, 0, item);
                    });
                    return newArray;
                });
            }
            if (((_d = data_1.tables) === null || _d === void 0 ? void 0 : _d.length) > 0) {
                (_e = setters.setTable) === null || _e === void 0 ? void 0 : _e.call(setters, function (prev) {
                    var newArray = __spreadArray([], prev, true);
                    __spreadArray([], data_1.tables, true).sort(function (a, b) { return b.index - a.index; }).forEach(function (_a) {
                        var index = _a.index, item = _a.data;
                        newArray.splice(index, 0, item);
                    });
                    return newArray;
                });
            }
            if (((_f = data_1.manipulatives) === null || _f === void 0 ? void 0 : _f.length) > 0) {
                setters.setManipulative(function (prev) {
                    var newArray = __spreadArray([], prev, true);
                    __spreadArray([], data_1.manipulatives, true).sort(function (a, b) { return b.index - a.index; }).forEach(function (_a) {
                        var item = _a.item, index = _a.index;
                        newArray.splice(index, 0, item);
                    });
                    return newArray;
                });
            }
        }
        else if (propertyName === 'multipleDelete') {
            // Restore multiple deleted items at their original positions
            var deletedItemsWithPosition_1 = typeof old === 'string' ? JSON.parse(old) : old;
            if (type === 'multipleManipulatives') {
                setters.setManipulative(function (prev) {
                    var newArray = __spreadArray([], prev, true);
                    // Sort by index in descending order to insert from end to start
                    // This prevents index shifting issues
                    var sorted = __spreadArray([], deletedItemsWithPosition_1, true).sort(function (a, b) { return b.index - a.index; });
                    sorted.forEach(function (_a) {
                        var item = _a.item, index = _a.index;
                        newArray.splice(index, 0, item);
                    });
                    return newArray;
                });
            }
        }
        else if (propertyName === 'multipleDuplicate') {
            // Remove multiple duplicated items (undo duplication)
            var duplicatedItems_1 = typeof old === 'string' ? JSON.parse(old) : old;
            if (type === 'multipleManipulatives') {
                setters.setManipulative(function (prev) {
                    // Remove all duplicated items by their IDs
                    var idsToRemove = new Set(duplicatedItems_1.map(function (entry) {
                        return entry.duplicate.data.id;
                    }));
                    return prev.filter(function (m) { return !idsToRemove.has(m.data.id); });
                });
            }
        }
        else if (propertyName === 'multipleCompleteData') {
            // Undo multiple completeData changes - restore old data for each affected item
            var oldItems_1 = typeof old === 'string' ? JSON.parse(old) : old;
            if (type === 'multipleManipulatives') {
                setters.setManipulative(function (prev) {
                    return prev.map(function (m) {
                        var oldItem = oldItems_1.find(function (item) { return item.data.id === m.data.id; });
                        if (!oldItem)
                            return m;
                        return __assign(__assign({}, m), { data: oldItem.data });
                    });
                });
            }
        }
        else if (propertyName === 'batchCreate') {
            // Remove all batch-created items (undo batch creation)
            var batchData = typeof old === 'string' ? JSON.parse(old) : old;
            if (Array.isArray(batchData)) {
                var ids_1 = batchData.map(function (item) { var _a; return (_a = item.data) === null || _a === void 0 ? void 0 : _a.id; }).filter(Boolean);
                setters.setManipulative(function (prev) { return prev.filter(function (m) { return !ids_1.includes(m.data.id); }); });
            }
        }
        else if (propertyName === 'create') {
            // Remove created item (undo creation)
            if (type === 'table') {
                (_g = setters.setTable) === null || _g === void 0 ? void 0 : _g.call(setters, function (prev) { return prev.filter(function (_, index) { return index !== Number(id); }); });
            }
            else if (type.startsWith('drawCommand')) {
                setters.setDrawCommands(function (prev) { return prev.filter(function (_, index) { return index !== Number(id); }); });
            }
            else if (type.startsWith('annotations')) {
                setters.setAnnotations(function (prev) { return prev.filter(function (_, index) { return index !== Number(id); }); });
            }
            else if (type.startsWith('text') || type.startsWith('mathtext')) {
                setters.setText(function (prev) { return prev.filter(function (_, index) { return index !== Number(id); }); });
            }
            else if (type.startsWith('shades')) {
                setters.setShades(function (prev) { return prev.filter(function (_, index) { return index !== Number(id); }); });
            }
            else if (type.startsWith('dice') ||
                type.startsWith('spinner') ||
                type.startsWith('patternShapes') ||
                type.startsWith('numberChart') ||
                type.startsWith('timer') ||
                type.startsWith('analog') ||
                type.startsWith('fractionTiles') ||
                // type.startsWith('manipulativeGroup') ||
                type.startsWith('nmpGroup') ||
                type.startsWith('deck') ||
                type.startsWith('arrayModel') ||
                type.startsWith('numberRacks') ||
                type.startsWith('linkingCube') ||
                type.startsWith('numberPiece') ||
                type.startsWith('moneyPiece') ||
                type.startsWith('coloredTiles') ||
                type.startsWith('linearPiece') ||
                type.startsWith('jumpBar') ||
                type.startsWith('customTick') ||
                type.startsWith('numberLine') ||
                type.startsWith('numberFrame_') ||
                type.startsWith('counter_') ||
                type.startsWith('numberFrameCounter_') ||
                type.startsWith('coinsAndCounters') ||
                type.startsWith('protractor')) {
                var oldData = typeof old === 'string' ? JSON.parse(old) : old;
                var itemId_1 = ((_h = oldData === null || oldData === void 0 ? void 0 : oldData.data) === null || _h === void 0 ? void 0 : _h.id) || String(id);
                setters.setManipulative(function (prev) { return prev.filter(function (m) { return m.data.id !== itemId_1; }); });
            }
        }
        else if (propertyName === 'delete') {
            // Restore deleted item
            var oldData_1 = typeof old === 'string' ? JSON.parse(old) : old;
            if (type === 'table') {
                (_j = setters.setTable) === null || _j === void 0 ? void 0 : _j.call(setters, function (prev) {
                    var newArray = __spreadArray([], prev, true);
                    newArray.splice(Number(id), 0, oldData_1);
                    return newArray;
                });
            }
            else if (type.startsWith('text') || type.startsWith('mathtext')) {
                setters.setText(function (prev) {
                    var newArray = __spreadArray([], prev, true);
                    newArray.splice(Number(id), 0, oldData_1);
                    return newArray;
                });
            }
            else if (type.startsWith('annotation')) {
                setters.setAnnotations(function (prev) {
                    var newArray = __spreadArray([], prev, true);
                    newArray.splice(Number(id), 0, oldData_1);
                    return newArray;
                });
            }
            else if (type.startsWith('shades')) {
                setters.setShades(function (prev) {
                    var newArray = __spreadArray([], prev, true);
                    newArray.splice(Number(id), 0, oldData_1);
                    return newArray;
                });
            }
            else if (type.startsWith('dice') ||
                type.startsWith('patternShapes') ||
                type.startsWith('spinner') ||
                type.startsWith('numberChart') ||
                type.startsWith('timer') ||
                type.startsWith('analog') ||
                type.startsWith('fractionTiles') ||
                type.startsWith('manipulativeGroup') ||
                type.startsWith('nmpGroup') ||
                type.startsWith('deck') ||
                type.startsWith('arrayModel') ||
                type.startsWith('numberRacks') ||
                type.startsWith('linkingCube') ||
                type.startsWith('numberPiece') ||
                type.startsWith('moneyPiece') ||
                type.startsWith('coloredTiles') ||
                type.startsWith('linearPiece') ||
                type.startsWith('jumpBar') ||
                type.startsWith('customTick') ||
                type.startsWith('numberLine') ||
                type.startsWith('numberFrame_') ||
                type.startsWith('counter_') ||
                type.startsWith('numberFrameCounter_') ||
                type.startsWith('coinsAndCounters') ||
                type.startsWith('protractor')) {
                setters.setManipulative(function (prev) {
                    var _a;
                    // Check if item already exists (avoid duplicates)
                    var itemId = String(id);
                    var fullId = (_a = oldData_1 === null || oldData_1 === void 0 ? void 0 : oldData_1.data) === null || _a === void 0 ? void 0 : _a.id;
                    var exists = prev.some(function (m) { return m && m.data && (m.data.id === itemId || (fullId && m.data.id === fullId)); });
                    if (!exists) {
                        if (!oldData_1 || !oldData_1.data) {
                            console.error('[undo delete] oldData is invalid, not restoring:', { type: type, id: id, oldData: oldData_1 });
                            return prev;
                        }
                        return __spreadArray(__spreadArray([], prev, true), [oldData_1], false);
                    }
                    return prev;
                });
            }
            else if (type.startsWith('drawCommand')) {
                // Restore deleted draw command
                setters.setDrawCommands(function (prev) {
                    var newArray = __spreadArray([], prev, true);
                    newArray.splice(Number(id), 0, oldData_1);
                    return newArray;
                });
            }
        }
        else if (propertyName === 'glue') {
            // Undo glue: Remove created glued tile and restore removed original tiles
            var _m = typeof old === 'string' ? JSON.parse(old) : old, removedTiles_1 = _m.removedTiles, createdTile_1 = _m.createdTile;
            setters.setManipulative(function (prev) { return __spreadArray(__spreadArray([], prev.filter(function (m) { return m.data.id !== createdTile_1.data.id; }), true), removedTiles_1, true); });
        }
        else if (propertyName === 'unglue') {
            // Undo unglue: Remove created restored tiles and restore removed glued tile
            var _o = typeof old === 'string' ? JSON.parse(old) : old, removedTile_1 = _o.removedTile, createdTiles = _o.createdTiles;
            var restoredIds_1 = new Set(createdTiles.map(function (t) { return t.data.id; }));
            setters.setManipulative(function (prev) { return __spreadArray(__spreadArray([], prev.filter(function (m) { return !restoredIds_1.has(m.data.id); }), true), [
                removedTile_1,
            ], false); });
        }
        else if (propertyName === 'split') {
            // Undo split: Remove created split tiles and restore removed parent tile
            var _p = typeof old === 'string' ? JSON.parse(old) : old, removedTile_2 = _p.removedTile, createdTiles = _p.createdTiles;
            var splitIds_1 = new Set(createdTiles.map(function (t) { return t.data.id; }));
            setters.setManipulative(function (prev) { return __spreadArray(__spreadArray([], prev.filter(function (m) { return !splitIds_1.has(m.data.id); }), true), [
                removedTile_2,
            ], false); });
        }
        else if (propertyName === 'duplicate') {
            // Remove duplicated item
            if (type === 'table') {
                (_k = setters.setTable) === null || _k === void 0 ? void 0 : _k.call(setters, function (prev) { return prev.filter(function (_, index) { return index !== Number(id); }); });
            }
            else if (type.startsWith('text') || type.startsWith('mathtext')) {
                setters.setText(function (prev) { return prev.filter(function (_, index) { return index !== Number(id); }); });
            }
            else if (type.startsWith('annotation')) {
                setters.setAnnotations(function (prev) { return prev.filter(function (_, index) { return index !== Number(id); }); });
            }
            else if (type.startsWith('shades')) {
                setters.setShades(function (prev) { return prev.filter(function (_, index) { return index !== Number(id); }); });
            }
            else if (type.startsWith('dice') ||
                type.startsWith('patternShapes') ||
                type.startsWith('spinner') ||
                type.startsWith('numberChart') ||
                type.startsWith('timer') ||
                type.startsWith('analog') ||
                type.startsWith('fractionTiles') ||
                type.startsWith('manipulativeGroup') ||
                type.startsWith('nmpGroup') ||
                type.startsWith('deck') ||
                type.startsWith('arrayModel') ||
                type.startsWith('numberRacks') ||
                type.startsWith('linkingCube') ||
                type.startsWith('numberPiece') ||
                type.startsWith('moneyPiece') ||
                type.startsWith('coloredTiles') ||
                type.startsWith('linearPiece') ||
                type.startsWith('jumpBar') ||
                type.startsWith('customTick') ||
                type.startsWith('numberLine') ||
                type.startsWith('numberFrame_') ||
                type.startsWith('counter_') ||
                type.startsWith('numberFrameCounter_') ||
                type.startsWith('coinsAndCounters') ||
                type.startsWith('protractor')) {
                // No-op here — handled by the batched setManipulative call below
                // that uses `duplicateManipulativeIdsToRemove`.
            }
            else if (type.startsWith('drawCommand')) {
                // Remove duplicated draw command
                setters.setDrawCommands(function (prev) { return prev.filter(function (_, index) { return index !== Number(id); }); });
            }
        }
        else if (propertyName === 'textContent') {
            // Whiteboard MathEditor text focus-out save.
            // old = text array before editing; new = full item object with updated text.
            var oldText_1 = typeof old === 'string' ? JSON.parse(old) : old;
            if (type.startsWith('text') || type.startsWith('mathtext')) {
                var isBlank = !oldText_1 ||
                    !Array.isArray(oldText_1) ||
                    oldText_1.length === 0 ||
                    oldText_1.every(function (item) {
                        if (item.type === 'math')
                            return !item.latex || item.latex.trim() === '';
                        return (item.value || '').replace(/<[^>]*>/g, '').replace(/\u200B/g, '').trim() === '';
                    });
                if (isBlank) {
                    // Old text was blank — undo means removing the item entirely
                    setters.setText(function (prev) { return prev.filter(function (_, i) { return i !== Number(id); }); });
                }
                else {
                    setters.setText(function (prev) {
                        var newArray = __spreadArray([], prev, true);
                        var idx = Number(id);
                        if (newArray[idx]) {
                            newArray[idx] = __assign(__assign({}, newArray[idx]), { data: __assign(__assign({}, newArray[idx].data), { text: oldText_1 }) });
                        }
                        return newArray;
                    });
                }
            }
        }
        else if (propertyName === 'groupCenters') {
            // Restore each group member's center from the pre-drag/pre-move snapshot map.
            var centersMap_1 = typeof old === 'string' ? JSON.parse(old) : old;
            setters.setManipulative(function (prev) {
                return prev.map(function (m) {
                    var memberId = m.data.id;
                    var center = memberId ? centersMap_1[memberId] : undefined;
                    if (!center)
                        return m;
                    return __assign(__assign({}, m), { data: __assign(__assign({}, m.data), { center: center }) });
                });
            });
        }
        else if (type === 'table') {
            // Restore table to old snapshot. `old` is sometimes a full { type, data }
            // snapshot (e.g. 'textEdit') and sometimes just the changed sub-value
            // (e.g. 'position' stores only { x, y, z }) — merge into `data` in that
            // case instead of replacing the whole entry, or `.data` gets wiped out.
            var oldValue_1 = typeof old === 'string' ? JSON.parse(old) : old;
            (_l = setters.setTable) === null || _l === void 0 ? void 0 : _l.call(setters, function (prev) {
                var _a;
                var newArray = __spreadArray([], prev, true);
                var index = Number(id);
                if (newArray[index]) {
                    if (oldValue_1 && typeof oldValue_1 === 'object' && 'data' in oldValue_1) {
                        newArray[index] = oldValue_1;
                    }
                    else {
                        newArray[index] = __assign(__assign({}, newArray[index]), { data: __assign(__assign({}, newArray[index].data), (_a = {}, _a[propertyName] = oldValue_1, _a)) });
                    }
                }
                return newArray;
            });
        }
        else {
            // Restore property value
            var oldValue_2 = typeof old === 'string' ? JSON.parse(old) : old;
            if (type.startsWith('text') || type.startsWith('mathtext')) {
                setters.setText(function (prev) {
                    var _a;
                    var newArray = __spreadArray([], prev, true);
                    var index = Number(id);
                    if (newArray[index]) {
                        if (propertyName === 'completeData') {
                            newArray[index] = __assign(__assign({}, newArray[index]), oldValue_2);
                        }
                        else {
                            newArray[index] = __assign(__assign({}, newArray[index]), { data: __assign(__assign({}, newArray[index].data), (_a = {}, _a[propertyName] = oldValue_2, _a)) });
                        }
                    }
                    return newArray;
                });
            }
            else if (type.startsWith('annotation')) {
                setters.setAnnotations(function (prev) {
                    var _a;
                    var newArray = __spreadArray([], prev, true);
                    var index = Number(id);
                    if (newArray[index]) {
                        if (propertyName === 'completeData') {
                            newArray[index] = __assign(__assign({}, newArray[index]), oldValue_2);
                        }
                        else {
                            newArray[index] = __assign(__assign({}, newArray[index]), { data: __assign(__assign({}, newArray[index].data), (_a = {}, _a[propertyName] = oldValue_2, _a)) });
                        }
                    }
                    return newArray;
                });
            }
            else if (type.startsWith('shades')) {
                setters.setShades(function (prev) {
                    var _a;
                    var newArray = __spreadArray([], prev, true);
                    var index = Number(id);
                    if (newArray[index]) {
                        if (propertyName === 'completeData') {
                            newArray[index] = __assign(__assign({}, newArray[index]), oldValue_2);
                        }
                        else {
                            newArray[index] = __assign(__assign({}, newArray[index]), { data: __assign(__assign({}, newArray[index].data), (_a = {}, _a[propertyName] = oldValue_2, _a)) });
                        }
                    }
                    return newArray;
                });
            }
            else if (type.startsWith('fractionTiles')) {
                setters.setManipulative(function (prev) {
                    var itemId = String(id);
                    return prev.map(function (m) {
                        var _a;
                        if (m.data.id === itemId || m.data.id === type) {
                            if (propertyName === 'completeData') {
                                return __assign(__assign({}, m), { data: __assign(__assign({}, m.data), oldValue_2) });
                            }
                            else {
                                return __assign(__assign({}, m), { data: __assign(__assign({}, m.data), (_a = {}, _a[propertyName] = oldValue_2, _a)) });
                            }
                        }
                        return m;
                    });
                });
            }
            else if (type.startsWith('dice') ||
                type.startsWith('patternShapes') ||
                type.startsWith('spinner') ||
                type.startsWith('numberChart') ||
                type.startsWith('timer') ||
                type.startsWith('analog') ||
                type.startsWith('deck') ||
                type.startsWith('arrayModel') ||
                type.startsWith('numberRacks') ||
                type.startsWith('linkingCube') ||
                type.startsWith('numberPiece') ||
                type.startsWith('moneyPiece') ||
                type.startsWith('coloredTiles') ||
                type.startsWith('linearPiece') ||
                type.startsWith('jumpBar') ||
                type.startsWith('customTick') ||
                type.startsWith('numberLine') ||
                type.startsWith('numberFrame_') ||
                type.startsWith('counter_') ||
                type.startsWith('numberFrameCounter_') ||
                type.startsWith('coinsAndCounters') ||
                type.startsWith('protractor')) {
                setters.setManipulative(function (prev) {
                    var _a;
                    var newArray = __spreadArray([], prev, true);
                    // id can be string or number, convert to string for comparison
                    var itemId = String(id);
                    var item = newArray.find(function (m) { return m.data.id === itemId || m.data.id === type; });
                    console.log({ item: item, propertyName: propertyName, oldValue: oldValue_2 }, 'undo item &&&');
                    if (item) {
                        if (propertyName === 'completeData') {
                            item.data = __assign(__assign({}, item.data), oldValue_2);
                            console.log(item.data, 'updated item data &&&');
                        }
                        else {
                            item.data = __assign(__assign({}, item.data), (_a = {}, _a[propertyName] = oldValue_2, _a));
                        }
                    }
                    return newArray;
                });
            }
            else if (type.startsWith('drawCommand')) {
                // Restore draw command property
                setters.setDrawCommands(function (prev) {
                    var _a;
                    var newArray = __spreadArray([], prev, true);
                    var index = Number(id);
                    if (newArray[index]) {
                        newArray[index] = __assign(__assign({}, newArray[index]), (_a = {}, _a[propertyName] = oldValue_2, _a));
                    }
                    return newArray;
                });
            }
            else if (type.startsWith('card')) {
                console.log('here in card &&&');
                setters.setManipulative(function (prev) {
                    return prev.map(function (m) {
                        if (m.data.type !== 'deck' || !('cards' in m.data))
                            return m;
                        var itemId = String(id);
                        var hasCard = m.data.cards.some(function (c) { return c.id === itemId || c.id === type; });
                        console.log(hasCard, m.data.cards, itemId, 'here in has card &&&');
                        if (!hasCard)
                            return m;
                        return __assign(__assign({}, m), { data: __assign(__assign({}, m.data), { cards: m.data.cards.map(function (card) {
                                    var _a;
                                    if (card.id !== itemId && card.id !== type)
                                        return card;
                                    if (propertyName === 'completeData') {
                                        console.log({ hasCard: hasCard, propertyName: propertyName, oldValue: oldValue_2 }, 'undo item &&&');
                                        return __assign(__assign({}, card), oldValue_2);
                                    }
                                    return __assign(__assign({}, card), (_a = {}, _a[propertyName] = oldValue_2, _a));
                                }) }) });
                    });
                });
            }
            else if (type.startsWith('manipulativeGroup') || type.startsWith('nmpGroup')) {
                setters.setManipulative(function (prev) {
                    var itemId = String(id);
                    if (propertyName === 'completeData') {
                        var updateMap_1 = new Map(oldValue_2.map(function (i) { return [i.data.id, i.data]; }));
                        return prev.map(function (m, _i) {
                            // const item = newArray.find(m => m.data.id === itemId || m.data.id === type);
                            console.log('group id redo &&&', m.data.groupId, itemId, type);
                            // if (m.data.groupId !== itemId && m.data.groupId !== type) {
                            //   return m;
                            // }
                            if (!updateMap_1.has(m.data.id))
                                return m;
                            var previousData = updateMap_1.get(m.data.id);
                            console.log('_newStateItem redo &&&', _i, oldValue_2, previousData);
                            return __assign(__assign({}, m), { data: __assign({}, previousData) });
                        });
                    }
                    else {
                        return prev.map(function (m) {
                            // const item = newArray.find(m => m.data.id === itemId || m.data.id === type);
                            var _a;
                            console.log('group id &&&', m.data.groupId, itemId, type, m.data.groupId !== itemId && m.data.groupId !== type);
                            if (m.data.groupId !== itemId && m.data.groupId !== type) {
                                return m;
                            }
                            return __assign(__assign({}, m), { data: __assign(__assign({}, m.data), (_a = {}, _a[propertyName] = oldValue_2, _a)) });
                        });
                    }
                });
            }
        }
    });
    // Single batched filter for all duplicate-undo manipulative removals.
    // Doing this AFTER the per-change loop guarantees the parent NL and its
    // cascade-child JBs / customTicks are removed atomically — the prior
    // per-entry setManipulative loop could in some cases skip some of the
    // child filters, leaving the duplicate NL still on the canvas.
    if (duplicateManipulativeIdsToRemove.size > 0) {
        setters.setManipulative(function (prev) {
            return prev.filter(function (m) { return !duplicateManipulativeIdsToRemove.has(m.data.id); });
        });
    }
}
/**
 * Apply redo operation by restoring new values from state changes
 * @param stateChanges - Array of state changes from redo()
 * @param setters - Object containing state setter functions
 */
export function applyRedo(stateChanges, setters) {
    stateChanges.forEach(function (change) {
        var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k;
        var type = change.type, id = change.id, propName = change.propertyName, newValue = change.new, old = change.old;
        var propertyName = propName;
        console.log('applyRedo', change);
        if (propertyName === 'reorder') {
            // Re-apply the bring-to-front reorder
            var idOrder_2 = (typeof newValue === 'string' ? JSON.parse(newValue) : newValue);
            setters.setManipulative(function (prev) { return reorderByIds(prev, idOrder_2); });
            return;
        }
        if (propertyName === 'mixedDelete') {
            // Re-delete all items (redo the mixed deletion)
            var data = typeof old === 'string' ? JSON.parse(old) : old;
            if (((_a = data.texts) === null || _a === void 0 ? void 0 : _a.length) > 0) {
                var indicesToDelete_1 = new Set(data.texts.map(function (d) { return d.index; }));
                setters.setText(function (prev) { return prev.filter(function (_, idx) { return !indicesToDelete_1.has(idx); }); });
            }
            if (((_b = data.annotations) === null || _b === void 0 ? void 0 : _b.length) > 0) {
                var indicesToDelete_2 = new Set(data.annotations.map(function (d) { return d.index; }));
                setters.setAnnotations(function (prev) { return prev.filter(function (_, idx) { return !indicesToDelete_2.has(idx); }); });
            }
            if (((_c = data.shades) === null || _c === void 0 ? void 0 : _c.length) > 0) {
                var indicesToDelete_3 = new Set(data.shades.map(function (d) { return d.index; }));
                setters.setShades(function (prev) { return prev.filter(function (_, idx) { return !indicesToDelete_3.has(idx); }); });
            }
            if (((_d = data.tables) === null || _d === void 0 ? void 0 : _d.length) > 0) {
                var indicesToDelete_4 = new Set(data.tables.map(function (d) { return d.index; }));
                (_e = setters.setTable) === null || _e === void 0 ? void 0 : _e.call(setters, function (prev) { return prev.filter(function (_, idx) { return !indicesToDelete_4.has(idx); }); });
            }
            if (((_f = data.manipulatives) === null || _f === void 0 ? void 0 : _f.length) > 0) {
                var idsToDelete_1 = new Set(data.manipulatives.map(function (entry) { return entry.item.data.id; }));
                setters.setManipulative(function (prev) { return prev.filter(function (m) { return !idsToDelete_1.has(m.data.id); }); });
            }
        }
        else if (propertyName === 'multipleDelete') {
            // Delete multiple items again
            var deletedItemsWithPosition_2 = typeof old === 'string' ? JSON.parse(old) : old;
            if (type === 'multipleManipulatives') {
                setters.setManipulative(function (prev) {
                    // Remove all the items that were deleted
                    var idsToDelete = new Set(deletedItemsWithPosition_2.map(function (entry) { return entry.item.data.id; }));
                    return prev.filter(function (m) { return !idsToDelete.has(m.data.id); });
                });
            }
        }
        else if (propertyName === 'multipleDuplicate') {
            // Add multiple duplicated items again (redo duplication)
            var duplicatedItems_2 = typeof old === 'string' ? JSON.parse(old) : old;
            if (type === 'multipleManipulatives') {
                setters.setManipulative(function (prev) {
                    // Add all duplicated items back
                    var itemsToAdd = duplicatedItems_2.map(function (entry) {
                        return entry.duplicate;
                    });
                    // Check for existing items to avoid duplicates
                    var existingIds = new Set(prev.map(function (m) { return m.data.id; }));
                    var newItems = itemsToAdd.filter(function (item) { return !existingIds.has(item.data.id); });
                    return __spreadArray(__spreadArray([], prev, true), newItems, true);
                });
            }
        }
        else if (propertyName === 'multipleCompleteData') {
            // Redo multiple completeData changes - apply new data for each affected item
            var newItems_1 = typeof newValue === 'string' ? JSON.parse(newValue) : newValue;
            if (type === 'multipleManipulatives') {
                setters.setManipulative(function (prev) {
                    return prev.map(function (m) {
                        var newItem = newItems_1.find(function (item) { return item.data.id === m.data.id; });
                        if (!newItem)
                            return m;
                        return __assign(__assign({}, m), { data: newItem.data });
                    });
                });
            }
        }
        else if (propertyName === 'batchCreate') {
            // Add all batch-created items back (redo batch creation)
            var batchData_1 = typeof old === 'string' ? JSON.parse(old) : old;
            if (Array.isArray(batchData_1)) {
                setters.setManipulative(function (prev) {
                    var existingIds = prev.map(function (m) { return m.data.id; });
                    var newItems = batchData_1.filter(function (item) { var _a; return !existingIds.includes((_a = item.data) === null || _a === void 0 ? void 0 : _a.id); });
                    return __spreadArray(__spreadArray([], prev, true), newItems, true);
                });
            }
        }
        else if (propertyName === 'create') {
            // Add created item again (redo creation)
            if (type === 'table') {
                var newData_1 = typeof old === 'string' ? JSON.parse(old) : old;
                (_g = setters.setTable) === null || _g === void 0 ? void 0 : _g.call(setters, function (prev) {
                    var newArray = __spreadArray([], prev, true);
                    newArray.splice(Number(id), 0, newData_1);
                    return newArray;
                });
            }
            else if (type.startsWith('drawCommand')) {
                var newData_2 = typeof old === 'string' ? JSON.parse(old) : old;
                setters.setDrawCommands(function (prev) {
                    var newArray = __spreadArray([], prev, true);
                    newArray.splice(Number(id), 0, newData_2);
                    return newArray;
                });
            }
            else if (type.startsWith('annotations')) {
                var newData_3 = typeof old === 'string' ? JSON.parse(old) : old;
                console.log(old, newData_3);
                setters.setAnnotations(function (prev) {
                    var newArray = __spreadArray([], prev, true);
                    newArray.splice(Number(id), 0, newData_3);
                    return newArray;
                });
            }
            else if (type.startsWith('text') || type.startsWith('mathtext')) {
                var newData_4 = typeof old === 'string' ? JSON.parse(old) : old;
                setters.setText(function (prev) {
                    var newArray = __spreadArray([], prev, true);
                    newArray.splice(Number(id), 0, newData_4);
                    return newArray;
                });
            }
            else if (type.startsWith('shades')) {
                var newData_5 = typeof old === 'string' ? JSON.parse(old) : old;
                setters.setShades(function (prev) {
                    var newArray = __spreadArray([], prev, true);
                    newArray.splice(Number(id), 0, newData_5);
                    return newArray;
                });
            }
            else if (type.startsWith('dice') ||
                type.startsWith('patternShapes') ||
                type.startsWith('spinner') ||
                type.startsWith('numberChart') ||
                type.startsWith('timer') ||
                type.startsWith('analog') ||
                type.startsWith('fractionTiles') ||
                // type.startsWith('manipulativeGroup') ||
                type.startsWith('nmpGroup') ||
                type.startsWith('deck') ||
                type.startsWith('arrayModel') ||
                type.startsWith('numberRacks') ||
                type.startsWith('linkingCube') ||
                type.startsWith('numberPiece') ||
                type.startsWith('moneyPiece') ||
                type.startsWith('coloredTiles') ||
                type.startsWith('linearPiece') ||
                type.startsWith('jumpBar') ||
                type.startsWith('customTick') ||
                type.startsWith('numberLine') ||
                type.startsWith('numberFrame_') ||
                type.startsWith('counter_') ||
                type.startsWith('numberFrameCounter_') ||
                type.startsWith('coinsAndCounters') ||
                type.startsWith('protractor')) {
                var newData_6 = typeof old === 'string' ? JSON.parse(old) : old;
                setters.setManipulative(function (prev) {
                    var _a;
                    var itemId = ((_a = newData_6 === null || newData_6 === void 0 ? void 0 : newData_6.data) === null || _a === void 0 ? void 0 : _a.id) || String(id);
                    var exists = prev.some(function (m) { return m.data.id === itemId; });
                    if (!exists) {
                        return __spreadArray(__spreadArray([], prev, true), [newData_6], false);
                    }
                    return prev;
                });
            }
        }
        else if (propertyName === 'delete') {
            // Delete item again
            if (type === 'table') {
                (_h = setters.setTable) === null || _h === void 0 ? void 0 : _h.call(setters, function (prev) { return prev.filter(function (_, index) { return index !== Number(id); }); });
            }
            else if (type.startsWith('text') || type.startsWith('mathtext')) {
                setters.setText(function (prev) { return prev.filter(function (_, index) { return index !== Number(id); }); });
            }
            else if (type.startsWith('annotation')) {
                setters.setAnnotations(function (prev) { return prev.filter(function (_, index) { return index !== Number(id); }); });
            }
            else if (type.startsWith('shades')) {
                setters.setShades(function (prev) { return prev.filter(function (_, index) { return index !== Number(id); }); });
            }
            else if (type.startsWith('dice') ||
                type.startsWith('patternShapes') ||
                type.startsWith('spinner') ||
                type.startsWith('numberChart') ||
                type.startsWith('timer') ||
                type.startsWith('analog') ||
                type.startsWith('fractionTiles') ||
                type.startsWith('manipulativeGroup') ||
                type.startsWith('nmpGroup') ||
                type.startsWith('deck') ||
                type.startsWith('arrayModel') ||
                type.startsWith('numberRacks') ||
                type.startsWith('linkingCube') ||
                type.startsWith('numberPiece') ||
                type.startsWith('moneyPiece') ||
                type.startsWith('coloredTiles') ||
                type.startsWith('linearPiece') ||
                type.startsWith('jumpBar') ||
                type.startsWith('customTick') ||
                type.startsWith('numberLine') ||
                type.startsWith('numberFrame_') ||
                type.startsWith('counter_') ||
                type.startsWith('numberFrameCounter_') ||
                type.startsWith('coinsAndCounters') ||
                type.startsWith('protractor')) {
                setters.setManipulative(function (prev) {
                    var fullId = String(type);
                    var itemId = String(id);
                    return prev.filter(function (m) { return m.data.id !== fullId && m.data.id !== itemId; });
                });
            }
            else if (type.startsWith('drawCommand')) {
                // Delete draw command again
                setters.setDrawCommands(function (prev) { return prev.filter(function (_, index) { return index !== Number(id); }); });
            }
        }
        else if (propertyName === 'glue') {
            // Redo glue: Remove original tiles and restore glued tile
            var _l = typeof old === 'string' ? JSON.parse(old) : old, removedTiles = _l.removedTiles, createdTile_2 = _l.createdTile;
            var removedIds_1 = new Set(removedTiles.map(function (t) { return t.data.id; }));
            setters.setManipulative(function (prev) { return __spreadArray(__spreadArray([], prev.filter(function (m) { return !removedIds_1.has(m.data.id); }), true), [
                createdTile_2,
            ], false); });
        }
        else if (propertyName === 'unglue') {
            // Redo unglue: Remove glued tile and restore individual tiles
            var _m = typeof old === 'string' ? JSON.parse(old) : old, removedTile_3 = _m.removedTile, createdTiles_1 = _m.createdTiles;
            setters.setManipulative(function (prev) { return __spreadArray(__spreadArray([], prev.filter(function (m) { return m.data.id !== removedTile_3.data.id; }), true), createdTiles_1, true); });
        }
        else if (propertyName === 'split') {
            // Redo split: Remove parent tile and restore split tiles
            var _o = typeof old === 'string' ? JSON.parse(old) : old, removedTile_4 = _o.removedTile, createdTiles_2 = _o.createdTiles;
            setters.setManipulative(function (prev) { return __spreadArray(__spreadArray([], prev.filter(function (m) { return m.data.id !== removedTile_4.data.id; }), true), createdTiles_2, true); });
        }
        else if (propertyName === 'duplicate') {
            // Add duplicated item again
            // For duplicate, the new item data is stored in 'old' field
            var newData_7 = newValue
                ? typeof newValue === 'string'
                    ? JSON.parse(newValue)
                    : newValue
                : typeof change.old === 'string'
                    ? JSON.parse(change.old)
                    : change.old;
            if (type === 'table') {
                (_j = setters.setTable) === null || _j === void 0 ? void 0 : _j.call(setters, function (prev) { return __spreadArray(__spreadArray([], prev, true), [newData_7], false); });
            }
            else if (type.startsWith('text') || type.startsWith('mathtext')) {
                setters.setText(function (prev) { return __spreadArray(__spreadArray([], prev, true), [newData_7], false); });
            }
            else if (type.startsWith('annotation')) {
                setters.setAnnotations(function (prev) { return __spreadArray(__spreadArray([], prev, true), [newData_7], false); });
            }
            else if (type.startsWith('shades')) {
                setters.setShades(function (prev) { return __spreadArray(__spreadArray([], prev, true), [newData_7], false); });
            }
            else if (type.startsWith('dice') ||
                type.startsWith('patternShapes') ||
                type.startsWith('spinner') ||
                type.startsWith('numberChart') ||
                type.startsWith('timer') ||
                type.startsWith('analog') ||
                type.startsWith('fractionTiles') ||
                type.startsWith('manipulativeGroup') ||
                type.startsWith('nmpGroup') ||
                type.startsWith('deck') ||
                type.startsWith('arrayModel') ||
                type.startsWith('numberRacks') ||
                type.startsWith('linkingCube') ||
                type.startsWith('numberPiece') ||
                type.startsWith('moneyPiece') ||
                type.startsWith('coloredTiles') ||
                type.startsWith('linearPiece') ||
                type.startsWith('jumpBar') ||
                type.startsWith('customTick') ||
                type.startsWith('numberLine') ||
                type.startsWith('numberFrame_') ||
                type.startsWith('counter_') ||
                type.startsWith('numberFrameCounter_') ||
                type.startsWith('coinsAndCounters') ||
                type.startsWith('protractor')) {
                setters.setManipulative(function (prev) {
                    var itemId = String(id);
                    // Check if item already exists (avoid duplicates)
                    var exists = prev.some(function (m) { return m.data.id === itemId; });
                    if (!exists) {
                        return __spreadArray(__spreadArray([], prev, true), [newData_7], false);
                    }
                    return prev;
                });
            }
            else if (type.startsWith('drawCommand')) {
                // Add duplicated draw command again
                setters.setDrawCommands(function (prev) { return __spreadArray(__spreadArray([], prev, true), [newData_7], false); });
            }
        }
        else if (propertyName === 'textContent') {
            // Whiteboard MathEditor text focus-out save.
            // new = full item object with updated text; if item doesn't exist at index, recreate it.
            var newItem_1 = typeof newValue === 'string' ? JSON.parse(newValue) : newValue;
            if (type.startsWith('text') || type.startsWith('mathtext')) {
                setters.setText(function (prev) {
                    var _a;
                    var idx = Number(id);
                    if (prev[idx] === undefined) {
                        // Item was removed (undo removed it) — recreate it at the original index
                        var newArray = __spreadArray([], prev, true);
                        newArray.splice(idx, 0, newItem_1);
                        return newArray;
                    }
                    else {
                        // Item exists — update only its text content
                        var newArray = __spreadArray([], prev, true);
                        newArray[idx] = __assign(__assign({}, newArray[idx]), { data: __assign(__assign({}, newArray[idx].data), { text: (_a = newItem_1.data) === null || _a === void 0 ? void 0 : _a.text }) });
                        return newArray;
                    }
                });
            }
        }
        else if (propertyName === 'groupCenters') {
            // Re-apply each group member's center from the post-drag/post-move snapshot map.
            var centersMap_2 = typeof newValue === 'string' ? JSON.parse(newValue) : newValue;
            setters.setManipulative(function (prev) {
                return prev.map(function (m) {
                    var memberId = m.data.id;
                    var center = memberId ? centersMap_2[memberId] : undefined;
                    if (!center)
                        return m;
                    return __assign(__assign({}, m), { data: __assign(__assign({}, m.data), { center: center }) });
                });
            });
        }
        else if (type === 'table') {
            // Apply new table snapshot. `new` is sometimes a full { type, data }
            // snapshot and sometimes just the changed sub-value (e.g. 'position'
            // stores only { x, y, z }) — merge into `data` in that case instead of
            // replacing the whole entry, or `.data` gets wiped out.
            var value_1 = typeof newValue === 'string' ? JSON.parse(newValue) : newValue;
            (_k = setters.setTable) === null || _k === void 0 ? void 0 : _k.call(setters, function (prev) {
                var _a;
                var newArray = __spreadArray([], prev, true);
                var index = Number(id);
                if (newArray[index]) {
                    if (value_1 && typeof value_1 === 'object' && 'data' in value_1) {
                        newArray[index] = value_1;
                    }
                    else {
                        newArray[index] = __assign(__assign({}, newArray[index]), { data: __assign(__assign({}, newArray[index].data), (_a = {}, _a[propertyName] = value_1, _a)) });
                    }
                }
                return newArray;
            });
        }
        else {
            // Apply new property value
            var value_2 = typeof newValue === 'string' ? JSON.parse(newValue) : newValue;
            if (type.startsWith('text') || type.startsWith('mathtext')) {
                setters.setText(function (prev) {
                    var _a;
                    var newArray = __spreadArray([], prev, true);
                    var index = Number(id);
                    if (newArray[index]) {
                        if (propertyName === 'completeData') {
                            newArray[index] = __assign(__assign({}, newArray[index]), value_2);
                        }
                        else {
                            newArray[index] = __assign(__assign({}, newArray[index]), { data: __assign(__assign({}, newArray[index].data), (_a = {}, _a[propertyName] = value_2, _a)) });
                        }
                    }
                    return newArray;
                });
            }
            else if (type.startsWith('annotation')) {
                setters.setAnnotations(function (prev) {
                    var _a;
                    var newArray = __spreadArray([], prev, true);
                    var index = Number(id);
                    if (newArray[index]) {
                        if (propertyName === 'completeData') {
                            newArray[index] = __assign(__assign({}, newArray[index]), value_2);
                        }
                        else {
                            newArray[index] = __assign(__assign({}, newArray[index]), { data: __assign(__assign({}, newArray[index].data), (_a = {}, _a[propertyName] = value_2, _a)) });
                        }
                    }
                    return newArray;
                });
            }
            else if (type.startsWith('shades')) {
                setters.setShades(function (prev) {
                    var _a;
                    var newArray = __spreadArray([], prev, true);
                    var index = Number(id);
                    if (newArray[index]) {
                        if (propertyName === 'completeData') {
                            newArray[index] = __assign(__assign({}, newArray[index]), value_2);
                        }
                        else {
                            newArray[index] = __assign(__assign({}, newArray[index]), { data: __assign(__assign({}, newArray[index].data), (_a = {}, _a[propertyName] = value_2, _a)) });
                        }
                    }
                    return newArray;
                });
            }
            else if (type.startsWith('fractionTiles')) {
                setters.setManipulative(function (prev) {
                    var itemId = String(id);
                    return prev.map(function (m) {
                        var _a;
                        if (m.data.id === itemId || m.data.id === type) {
                            if (propertyName === 'completeData') {
                                return __assign(__assign({}, m), { data: __assign(__assign({}, m.data), value_2) });
                            }
                            else {
                                return __assign(__assign({}, m), { data: __assign(__assign({}, m.data), (_a = {}, _a[propertyName] = value_2, _a)) });
                            }
                        }
                        return m;
                    });
                });
            }
            else if (type.startsWith('dice') ||
                type.startsWith('patternShapes') ||
                type.startsWith('spinner') ||
                type.startsWith('numberChart') ||
                type.startsWith('timer') ||
                type.startsWith('analog') ||
                type.startsWith('deck') ||
                type.startsWith('arrayModel') ||
                type.startsWith('numberRacks') ||
                type.startsWith('linkingCube') ||
                type.startsWith('numberPiece') ||
                type.startsWith('moneyPiece') ||
                type.startsWith('coloredTiles') ||
                type.startsWith('linearPiece') ||
                type.startsWith('jumpBar') ||
                type.startsWith('customTick') ||
                type.startsWith('numberLine') ||
                type.startsWith('numberFrame_') ||
                type.startsWith('counter_') ||
                type.startsWith('numberFrameCounter_') ||
                type.startsWith('coinsAndCounters') ||
                type.startsWith('protractor')) {
                setters.setManipulative(function (prev) {
                    var _a;
                    var newArray = __spreadArray([], prev, true);
                    // id can be string or number, convert to string for comparison
                    var itemId = String(id);
                    var item = newArray.find(function (m) { return m.data.id === itemId || m.data.id === type; });
                    // For completeData, also match by the old snapshot's id — handles cases where undo
                    // restored the item to a different id (e.g. onOutline id change from _dummyForRightTool
                    // back to original), so redo can still locate it after the id was reversed.
                    if (!item && propertyName === 'completeData') {
                        var oldSnapshotId_1 = (function () {
                            var _a;
                            try {
                                return (_a = JSON.parse(String(old))) === null || _a === void 0 ? void 0 : _a.id;
                            }
                            catch (_b) {
                                return undefined;
                            }
                        })();
                        if (oldSnapshotId_1)
                            item = newArray.find(function (m) { return m.data.id === oldSnapshotId_1; });
                    }
                    console.log({ item: item, propertyName: propertyName, value: value_2 }, 'undo item &&&');
                    if (item) {
                        if (propertyName === 'completeData') {
                            item.data = __assign(__assign({}, item.data), value_2);
                        }
                        else {
                            item.data = __assign(__assign({}, item.data), (_a = {}, _a[propertyName] = value_2, _a));
                        }
                    }
                    return newArray;
                });
            }
            else if (type.startsWith('drawCommand')) {
                // Apply new draw command property value
                setters.setDrawCommands(function (prev) {
                    var _a;
                    var newArray = __spreadArray([], prev, true);
                    var index = Number(id);
                    if (newArray[index]) {
                        newArray[index] = __assign(__assign({}, newArray[index]), (_a = {}, _a[propertyName] = value_2, _a));
                    }
                    return newArray;
                });
            }
            else if (type.startsWith('card')) {
                setters.setManipulative(function (prev) {
                    return prev.map(function (m) {
                        if (m.data.type !== 'deck' || !('cards' in m.data))
                            return m;
                        var itemId = String(id);
                        var hasCard = m.data.cards.some(function (c) { return c.id === itemId || c.id === type; });
                        if (!hasCard)
                            return m;
                        return __assign(__assign({}, m), { data: __assign(__assign({}, m.data), { cards: m.data.cards.map(function (card) {
                                    var _a;
                                    if (card.id !== itemId && card.id !== type)
                                        return card;
                                    if (propertyName === 'completeData') {
                                        console.log({ hasCard: hasCard, propertyName: propertyName, value: value_2 }, 'undo item &&&');
                                        return __assign(__assign({}, card), value_2);
                                    }
                                    return __assign(__assign({}, card), (_a = {}, _a[propertyName] = value_2, _a));
                                }) }) });
                    });
                });
            }
            else if (type.startsWith('manipulativeGroup') || type.startsWith('nmpGroup')) {
                setters.setManipulative(function (prev) {
                    var itemId = String(id);
                    console.log('value &&&', value_2);
                    if (propertyName === 'completeData') {
                        var updateMap_2 = new Map(value_2.map(function (i) { return [i.data.id, i.data]; }));
                        return prev.map(function (m) {
                            console.log('group id &&&', m, m.data.groupId, itemId, type);
                            if (!updateMap_2.has(m.data.id))
                                return m;
                            var previousData = updateMap_2.get(m.data.id);
                            var _newStateItem = __assign(__assign({}, m), { data: __assign({}, previousData) });
                            console.log('_newStateItem &&&', value_2, previousData);
                            return _newStateItem;
                        });
                    }
                    else {
                        return prev.map(function (m) {
                            // const item = newArray.find(m => m.data.id === itemId || m.data.id === type);
                            var _a;
                            console.log('group id &&&', m.data.groupId, itemId, type);
                            if (m.data.groupId !== itemId && m.data.groupId !== type) {
                                return m;
                            }
                            return __assign(__assign({}, m), { data: __assign(__assign({}, m.data), (_a = {}, _a[propertyName] = value_2, _a)) });
                        });
                    }
                });
            }
        }
    });
}
/**
 * Execute undo operation
 * @param setters - Object containing state setter functions
 * @returns true if undo was successful, false if nothing to undo
 */
export function executeUndo(setters) {
    var _a;
    var stateChanges = stateManager.undo();
    // eslint-disable-next-line no-console
    console.log('[undo/redo debug] executeUndo', JSON.stringify({ entryCount: (_a = stateChanges === null || stateChanges === void 0 ? void 0 : stateChanges.length) !== null && _a !== void 0 ? _a : 0, entries: stateChanges }));
    if (stateChanges) {
        applyUndo(stateChanges, setters);
        return true;
    }
    return false;
}
/**
 * Execute redo operation
 * @param setters - Object containing state setter functions
 * @returns true if redo was successful, false if nothing to redo
 */
export function executeRedo(setters) {
    var _a;
    var stateChanges = stateManager.redo();
    // eslint-disable-next-line no-console
    console.log('[undo/redo debug] executeRedo', JSON.stringify({ entryCount: (_a = stateChanges === null || stateChanges === void 0 ? void 0 : stateChanges.length) !== null && _a !== void 0 ? _a : 0, entries: stateChanges }));
    if (stateChanges) {
        applyRedo(stateChanges, setters);
        return true;
    }
    return false;
}
/**
 * Save state changes to the state manager
 * @param stateChange - Single state change or array of state changes
 */
// Map manipulative ID prefixes to readable names (translation keys)
var MANIPULATIVE_NAME_MAP = {
    analog_clock: 'text.manipulativeClock',
    timer_clock: 'text.manipulativeTimer',
    spinner: 'text.manipulativeSpinner',
    dice: 'text.manipulativeDice',
    deck: 'text.manipulativeCardDeck',
    numberChart: 'text.manipulativeNumberChart',
    numberLine: 'text.manipulativeNumberLine',
    counter: 'text.manipulativeCounter',
    numberFrame: 'text.manipulativeNumberFrame',
    linkingCubes: 'text.manipulativeLinkingCubes',
    fractionTile: 'text.manipulativeFractionTile',
    coloredTile: 'text.manipulativeColoredTile',
    patternBlock: 'text.manipulativePatternBlock',
    arrayModel: 'text.manipulativeArrayModel',
    coinsAndCounters: 'text.coinsAndCounters',
    protractor: 'text.manipulativeProtractor',
    annotations: 'text.annotation',
};
function getManipulativeNameKey(type) {
    for (var _a = 0, _b = Object.keys(MANIPULATIVE_NAME_MAP); _a < _b.length; _a++) {
        var prefix = _b[_a];
        if (type.startsWith(prefix))
            return MANIPULATIVE_NAME_MAP[prefix];
    }
    return null;
}
export function saveState(stateChange) {
    var asArray = Array.isArray(stateChange) ? stateChange : [stateChange];
    // eslint-disable-next-line no-console
    console.log('[undo/redo debug] saveState (new history entry pushed)', JSON.stringify({ entryCount: asArray.length, entries: asArray }));
    stateManager.save(stateChange);
    customEventHandler.dispatchEvent('stateChanged', { type: 'save' });
    // Mark creation for aria-label override on the new manipulative
    var entry = Array.isArray(stateChange) ? stateChange[0] : stateChange;
    if ((entry === null || entry === void 0 ? void 0 : entry.propertyName) === 'create') {
        markAsCreated(entry.type);
    }
}
/**
 * Reset the state manager
 */
export function resetState() {
    stateManager.reset();
}
export function getObjectByIndex(index) {
    return stateManager.getObjectByIndex(index);
}
export function getObjectByID(type, id) {
    return stateManager.getObjectByID(type, id);
}
/**
 * Returns the current history index so callers can record which entry
 * they just saved (for subsequent in-place updates via updateStateEntryNew).
 */
export function getStateCurrentIndex() {
    return stateManager.getCurrentIndex();
}
/**
 * Update the `new` property of an existing history entry in-place.
 * Use this after saveState() to patch the "new" value without pushing
 * a new undo step (e.g. text content updated on focus-out).
 */
export function updateStateEntryNew(historyIndex, stateChange) {
    stateManager.updateEntryNew(historyIndex, stateChange.type, stateChange.id, stateChange.propertyName, stateChange.new);
    customEventHandler.dispatchEvent('stateChanged', { type: 'save' });
}
export function canUndo() {
    return stateManager.canUndo();
}
export function canRedo() {
    return stateManager.canRedo();
}
/**
 * Check if reset is available (if there's any history to reset)
 */
export function canReset() {
    return stateManager.getHistoryCount() > 0;
}
